import type { en } from "./en";

// German translations. Missing keys fall back to English via i18next.
export const de: typeof en = {
  common: {
    home: "Start",
    no_scores: "Noch keine Punkte",
    points_count_one: "{{count}} Punkt",
    points_count_other: "{{count}} Punkte",
    questions_count_one: "{{count}} Frage",
    questions_count_other: "{{count}} Fragen",
  },
  home: {
    placeholder: "Spielcode",
    enter: "Mitmachen",
    error_invalid_code:
      "Gib den 6-stelligen Spielcode von deiner Lehrkraft ein.",
    footer: "Starte ein Binogi Buzz aus den Binogi.com-Produkten.",
    language_label: "Sprache",
  },
  errors: {
    generic: "Etwas ist schiefgelaufen. Bitte versuch es noch einmal.",
    stale_client: "Deine Seite ist veraltet – bitte lade sie neu.",
    session_not_found: "Diese Runde wurde nicht gefunden.",
    session_not_open: "Diese Runde ist beendet.",
    session_not_active: "Diese Frage oder Runde ist bereits beendet.",
    not_teacher: "Das kann nur die Lehrkraft.",
    already_answered: "Du hast diese Frage schon beantwortet.",
    wrong_question_index: "Diese Frage hat noch nicht begonnen.",
    name_taken: "Dieser Name ist schon vergeben. Versuch einen anderen.",
    character_busy: "Wir konnten dir gerade keinen Charakter geben – bitte versuch es noch einmal.",
  },
  host: {
    input: {
      heading: "Buzz starten",
      lead:
        "Gib den Binogi-Lektionscode ein, mit dem deine Schüler arbeiten. Wir laden die Fragen und erstellen eine Runde, die du an die Tafel werfen kannst.",
      lesson_code_label: "Lektionscode",
      placeholder_code: "BIH750",
      load_button: "Lektion laden",
      try_hint: "Probier:",
      error_invalid_code: "Gib einen Binogi-Lektionscode ein, z. B. BIH750.",
      error_not_found: "Keine Lektion mit dem Code {{code}} gefunden.",
      error_unreachable: "Der Lektionsserver ist nicht erreichbar. Prüfe deine Verbindung und versuch es noch einmal.",
      error_no_questions: "Die Lektion {{code}} hat keine Fragen, die Buzz verwenden kann.",
      error_no_level_questions: "Die Lektion {{code}} hat keine Fragen auf der gewählten Stufe.",
    },
    loading: "Lektion wird geladen…",
    placeholder_title: "Starte ein Binogi Buzz",
    tab_details: "Details",
    tab_questions: "Fragen",
    settings: "Einstellungen",
    number_of_students: "Anzahl der Schüler",
    fewer_students: "Weniger Schüler",
    more_students: "Mehr Schüler",
    player_names: "Spielernamen",
    player_anonymous: "Anonym",
    player_self_chosen: "Schüler wählen",
    language: "Sprache",
    language_teacher: "Lehrkraft wählt",
    language_students: "Schüler wählen",
    time_per_question: "Zeit pro Frage",
    time_option: "{{seconds}} s",
    start_button: "Runde starten",
    start_disabled: "Wähle die Klassengröße zum Starten",
    creating: "Runde wird erstellt…",
    error_no_questions: "Lass mindestens eine Frage übrig, bevor du startest.",
    error_create_failed: "Runde konnte nicht erstellt werden",
    error_load_failed: "Lektion konnte nicht geladen werden",
    no_session: "Keine Runde aktiv.",
    back_to_review: "Zurück zu den Fragen",
    errors: {
      dismiss: "Schließen",
      refresh: "Neu laden",
      attach_failed: "Dieser Bildschirm konnte sich nicht als Lehrkraft verbinden.",
      unreachable: "Der Server ist nicht erreichbar. Lade die Seite neu, um es noch einmal zu versuchen.",
      no_token: "Dieser Tab kann das Quiz nicht mehr steuern. Öffne Buzz aus deiner Lektion, um eine neue Runde zu starten.",
      taken_over: "Dieses Quiz wird jetzt von einem anderen Bildschirm aus gesteuert.",
      stale_question: "Buzz wurde während dieses Quiz aktualisiert, daher kann dieser Bildschirm es nicht fortsetzen. Lade die Seite neu, um das Quiz dort fortzusetzen, wo die Klasse gerade ist.",
      question_moved_on: "Das Quiz ist bereits weitergegangen, daher hängt dieser Bildschirm hinterher. Lade die Seite neu, um das Quiz dort fortzusetzen, wo die Klasse gerade ist.",
    },
    entry: {
      opening: "Lehreransicht wird geöffnet…",
      token_title: "Dieser Lehrerlink kann nicht geöffnet werden",
      token_body: "Dem Link fehlt der Lehrerschlüssel oder er ist abgelaufen. Öffne Buzz aus deiner Lektion, um eine neue Runde zu erstellen.",
      not_found_title: "Runde nicht gefunden",
      not_found_body: "Diese Quizrunde gibt es nicht mehr. Erstelle eine neue, um weiterzumachen.",
      back: "Zurück zum Start",
    },
    questions: {
      drag_aria: "Zum Sortieren ziehen",
      remove_aria: "Frage entfernen",
      empty: "Keine Fragen mehr in dieser Runde.",
      add: "Hinzufügen",
      add_aria: "Frage hinzufügen: {{text}}",
      add_all: "Alle hinzufügen",
      add_all_n: "Alle {{count}} Fragen hinzufügen",
      more_one: "{{count}} weitere Frage in dieser Lektion",
      more_other: "{{count}} weitere Fragen in dieser Lektion",
      level_easy: "Leicht",
      level_medium: "Mittel",
      level_hard: "Schwer",
      multi: "Mehrfach",
    },
    lobby: {
      go_to: "Geh auf <strong>buzz.binogi.com</strong> und gib ein:",
      or_qr: "…oder scanne den QR-Code",
      students_joined_of: "{{joined}} von {{expected}} Schülern sind dabei",
      students_joined_one: "{{count}} Schüler ist dabei",
      students_joined_other: "{{count}} Schüler sind dabei",
      waiting: "Warten, bis die Schüler beitreten…",
      start_game: "Spiel starten",
      connected: "Verbunden",
      connecting: "Verbinden…",
      copy_link: "Link kopieren",
      copy_link_copied: "Kopiert!",
    },
    question: {
      header: "Frage {{index}} von {{total}}",
      level: "Stufe {{level}}",
      none: "Keine aktive Frage.",
      answered: "Beantwortet",
      top_n: "Top {{count}}",
      answers_heading: "Antworten",
      end_question: "Frage beenden",
      next_question: "Nächste Frage",
      see_results: "Endergebnis ansehen",
    },
    results: {
      heading: "Endergebnis",
      all_players: "Alle Spieler",
      end_session: "Runde beenden",
      new_session: "Neue Runde",
      none: "Keine Spieler in dieser Runde.",
    },
  },
  join: {
    code_label: "Spielcode",
    name_label: "Dein Name",
    name_placeholder: "Gib deinen Namen ein",
    join_button: "Mitmachen",
    joining: "Beitreten…",
    checking: "Wird geprüft…",
    character_hint:
      "Du bekommst beim Beitreten einen lustigen Binogi-Charakternamen.",
    error_not_found:
      "Dieser Code gehört noch zu keiner laufenden Runde. Frag deine Lehrkraft.",
    error_lookup_failed: "Runde konnte nicht gefunden werden",
    error_code_required: "Gib den 6-stelligen Code ein.",
    error_name_required: "Gib deinen Namen ein.",
  },
  play: {
    superseded: "Du bist in einem anderen Tab verbunden — schließe ihn, um hier weiterzuspielen.",
    back_to_join: "Zurück zum Beitreten",
    no_round: "Keine Rundendaten.",
    not_joined: "Du bist keiner Runde beigetreten.",
    no_question: "Keine aktive Frage.",
    keep_awake_hint: "Tippe auf den Bildschirm, damit er anbleibt",
    waiting: {
      you_are: "Du bist",
      get_ready_named: "Mach dich bereit, {{name}}!",
      get_ready: "Mach dich bereit fürs Quiz!",
      in_room_label: "Schüler im Raum",
      in_room_one: "{{count}} Schüler im Raum",
      in_room_other: "{{count}} Schüler im Raum",
    },
    question: {
      progress: "{{index}} / {{total}}",
      answered: "Beantwortet!",
      times_up: "Zeit ist um",
      submit: "Absenden",
      submit_n: "{{count}} Antworten absenden",
    },
    between: {
      correct: "Richtig!",
      wrong: "Fast!",
      times_up: "Zeit ist um",
      points_gained: "+{{count}} Punkte",
      your_score: "Deine Punkte",
      your_rank: "Platz {{rank}}",
      top_n: "Top {{count}}",
      waiting_next: "Warte auf die nächste Frage…",
    },
    finished: {
      heading: "Danke fürs Mitspielen bei Binogi Buzz!",
      your_final_rank: "Deine Endplatzierung",
      top_n: "Top {{count}}",
      none: "Keine Ergebnisse zum Anzeigen.",
    },
  },
  characters: {
    heading: "Binogi-Charaktere",
    lead:
      "{{count}} Schüler bekommen beim Beitreten einen Charakter – in dieser Reihenfolge.",
  },
};
